<template>
  <div>
    <DashboardCard
      id="dashboard-comparables-card"
      :loading="loading"
      :expanded="expanded"
      :widget-type="widgetType"
      title="Recent Comps"
      :count="total"
      @update:expanded="$emit('update:expanded', $event)"
    >
      <template #actions>
        <v-btn
          v-if="$vuetify.display.mdAndDown"
          icon
          @click="filtersDrawer = true"
        >
          <v-icon>mdi-filter</v-icon>
        </v-btn>
        <DashboardCardFiltersFields
          v-else
          v-model="filters"
          source="comparable"
          date-label="Transaction Date:"
          :loading="loading"
          :filter-loading="filterLoading"
        />
      </template>
      <template #content>
        <v-skeleton-loader type="table" :loading="loading">
          <DashboardMobileTable
            v-if="$vuetify.display.mdAndDown"
            v-model:table-options="tableOptions"
            :items="items"
            :filter-loading="filterLoading"
            :total-rows-entry="total"
          >
            <template #item="{ item, index }">
              <ComparableMobileCard
                :item="item"
                :active-account-id="activeAccountId"
                :class="index === 0 ? 'mt-0' : 'mt-2'"
              />
            </template>
          </DashboardMobileTable>
          <BaseTable
            v-else
            :table-options-entry="tableOptions"
            :total-rows-entry="total"
            :headers-data-entry="headers"
            :items-entry="items"
            :loading="filterLoading"
            :custom-field="customField"
            :custom-field-items="customFieldItems"
            :source="customFieldSource"
            @options:change="tableOptions = $event"
          >
            <template #item="{ item }">
              <ComparablesListTableContent :props="item" :allow-select="false" :custom-field="customField" />
            </template>
          </BaseTable>
        </v-skeleton-loader>
      </template>
    </DashboardCard>
    <DashboardCardFilters
      v-if="$vuetify.display.mdAndDown"
      v-model="filters"
      v-model:open="filtersDrawer"
      source="comparable"
      title="Recent Comparable Filters"
      date-label="Transaction Date:"
      :loading="loading"
      :filter-loading="filterLoading"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { isEqual } from 'lodash-es';
import { mapState } from 'vuex';

import { addDates } from '@/utils/filters';
import { ServiceFactory } from '@/services/ServiceFactory';
import { IHeader, ITableOptions } from '@/bundles/BaseTable/interfaces';
import { getTableOptions } from '@/bundles/BaseTable/helpers/consts';
import { UserWidgetsEnum } from '@/bundles/Settings/enums/userLayoutEnum';
import { getComparablesHeadersData } from '@/bundles/Comparables/helpers';
import { getMarketColumns, IMarketColumns } from '@/bundles/Properties/helpers';
import { RecordsCardMixin } from '@/bundles/Common/mixins/RecordsCardMixin';
import { ComparableCustomFieldMixin } from '@/bundles/Comparables/mixins/ComparableCustomFieldMixin';
import { IDashboardCardFilters } from '@/bundles/Dashboard/interfaces';
import { getFormattedGroupFiltersArray } from '@/bundles/GroupFilters/helpers';
import { mapOldFiltersFromSetToNew } from '@/bundles/GroupFilters/helpers/mappers/mapOldFiltersFromSetToNew';
import { AllExcludeOnlyEnum } from '@/bundles/GroupFilters/enums/AllExcludeOnlyEnum';
import { PROPERTY_TYPE_KEY } from '@/bundles/GroupFilters/consts';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import BaseTable from '@/bundles/BaseTable/components/BaseTable.vue';
import DashboardCard from '@/bundles/Dashboard/components/DashboardCard.vue';
import ComparablesListTableContent from '@/bundles/Comparables/components/ComparablesListTableContent.vue';
import DashboardMobileTable from '@/bundles/Dashboard/components/DashboardMobileTable.vue';
import ComparableMobileCard from '@/bundles/Comparables/components/cards/ComparableMobileCard.vue';
import DashboardCardFilters from '@/bundles/Dashboard/components/filters/DashboardCardFilters.vue';
import DashboardCardFiltersFields from '@/bundles/Dashboard/components/filters/DashboardCardFiltersFields.vue';

const tableOptions = getTableOptions({
  itemsPerPage: 10,
  sortBy: ['created.date'],
  sortDesc: [true]
});

const ComparableService = ServiceFactory.get('comparable');

interface IDashboardComparableCardData {
  widgetType: UserWidgetsEnum;
  tableOptions: ITableOptions;
  isFirstLoad: boolean;
  filters: IDashboardCardFilters;
  filtersDrawer: boolean;
  filterParams: Record<string, any>;
}

export default defineComponent({
  name: 'DashboardComparablesCard',

  components: {
    ComparableMobileCard,
    DashboardMobileTable,
    ComparablesListTableContent,
    BaseTable,
    DashboardCard,
    DashboardCardFilters,
    DashboardCardFiltersFields
  },
  mixins: [RecordsCardMixin, ComparableCustomFieldMixin],

  props: {
    expanded: Boolean,
    sourceId: { // should be here to override prop from the mixin
      type: String,
      default: ''
    },
    sourceType: { // should be here to override prop from the mixin
      type: String,
      default: ''
    }
  },

  emits: ['update:expanded'],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: (): IDashboardComparableCardData => ({
    widgetType: UserWidgetsEnum.recent_comparables,
    tableOptions,
    isFirstLoad: true,
    filters: {
      inHouse: true,
      dateStart: addDates(new Date(Date.now()).toISOString(), { days: -14, }),
      dateEnd: new Date().toISOString().substr(0, 10),
      propertyType: null,
      savedSet: null,
    },
    filtersDrawer: false,
    filterParams: {},
  }),

  computed: {
    ...mapState({
      activeAccountId: (state: any): number => state.activeAccount.id,
      marketColumns: (state: any): IMarketColumns => getMarketColumns(state.activeAccount.id, state.accountSettings, 'comparable'),
    }),
    headers: function (): IHeader[] {
      return getComparablesHeadersData(this.marketColumns, false);
    },
  },
  watch: {
    tableOptions: {
      handler: function () {
        this.fetchComparables();
      },
      deep: true,
      immediate: true,
    },
    filters: {
      handler: function () {
        this.fetchComparables();
      }
    }
  },
  methods: {
    async fetchComparables () {
      try {
        this.filterLoading = true;
        // values must match the group filters wire format: date_range and multiple_select filters are arrays,
        // and the in-house filter is present only when restricting ('all' means no restriction on the list page)
        const cardFilters: Record<string, any> = {
          comparable_transactionDate: [this.filters.dateStart, this.filters.dateEnd],
        };
        if (this.filters.inHouse) {
          cardFilters.comparable_in_house = AllExcludeOnlyEnum.only;
        }
        if (this.filters.propertyType) {
          cardFilters[PROPERTY_TYPE_KEY] = [this.filters.propertyType];
        }

        const params: Record<string, any> = {
          pageOffset: this.pageOffset,
          pageLimit: this.tableOptions.itemsPerPage,
          sort: this.tableOptions.sortBy,
          descending: this.tableOptions.sortDesc,
          filters: [cardFilters],
        };

        if (this.filters.savedSet) {
          if (this.filters.savedSet.class === 'static') {
            params.setId = this.filters.savedSet.savedSet._key;
            params.filters = [{ ...cardFilters, setId: this.filters.savedSet.savedSet._key }];
          } else {
            params.savedSet = this.filters.savedSet.savedSet._key;
            const setGroups = getFormattedGroupFiltersArray(
              mapOldFiltersFromSetToNew(this.filters.savedSet.filter_groups || [], 'comparable')
            );
            // groups are OR'ed by the api, so the card's own filters are merged into each group to keep AND semantics
            params.filters = setGroups.length
              ? setGroups.map((group) => ({ ...group, ...cardFilters }))
              : [cardFilters];
          }
        }
        if (isEqual(this.filterParams, params)) {
          return;
        }

        if (!this.isFirstLoad) {
          document.getElementById('dashboard-comparables-card')?.scrollIntoView({ behavior: 'smooth' });
        }
        this.filterParams = { ...params };

        const { data } = await ComparableService.listGroupFilters({ token: this.token, params });

        this.items = data.result;
        this.total = data.total;
      } catch (error: any) {
        this.addNotification({ ...error });
      } finally {
        this.loading = false;
        this.filterLoading = false;
        this.isFirstLoad = false;
      }
    }
  },
})
</script>

<style lang="scss" scoped>
@use 'vuetify/settings' as *;

.comparable-card {
  height: auto !important;

  .v-toolbar__content {
    height: auto !important;
    flex-wrap: wrap;
  }
}

</style>
